import React from "react";
import { graphql } from "gatsby";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { GatsbyImage, getImage } from "gatsby-plugin-image";

import { theme } from "../../utils/global-styles/theme";

import BulletIcon from "../shared/BlurredContentContainer/Assets/BulletIcon";
import MobileCourseType from "./child-components/MobileCourseType";
import Link4Arrow from "../shared/Link4Arrow";

const CourseTypeBenefits = ({
  index,
  margin,
  mobileMargin,
  heading,
  description,
  link,
  benefit1,
  benefit2,
  benefit3,
  image,
  imageTint,
}) => {
  const isEvenIndex = index % 2 === 0,
    CourseTypeGrid = styled.div`
      height: 426px;
      display: grid;
      grid-template-rows: 1fr;
      margin: ${margin};
      font-family: ${theme.fontFaces.headers};

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        display: none;
      }
    `,
    TitleDescriptionLinkContainer = styled.div`
      padding-top: 100px;

      @media only screen and (max-width: ${theme.breakpoints.helper1100}) {
        padding-top: 70px;
      }
    `,
    CourseTypeHeading = styled.h2`
      font-size: 40px;
      font-weight: bold;
      letter-spacing: -1.04px;
      line-height: 48px;
      margin-bottom: 15px;
    `,
    CourseTypeDescription = styled.h3`
      font-size: 21px;
      font-weight: 500;
      letter-spacing: -0.45px;
      line-height: 32px;
      margin-bottom: 50px;
    `,
    BenefitsListContainer = styled.div`
      padding-top: 156px;

      @media only screen and (max-width: ${theme.breakpoints.helper1100}) {
        padding-top: 125px;
      }
    `,
    ListItemContainer = styled.div`
      display: flex;
      margin-bottom: 30px;
    `,
    BulletIconContainer = styled.div`
      height: max-content;
      width: max-content;
    `,
    ListItemText = styled.h3`
      font-size: 20px;
      font-weight: normal;
      line-height: 24px;
      margin-left: 12px;
    `,
    ImgContainer = styled.div`
      z-index: 1;
      overflow: hidden;
    `,
    StyledImg = styled(GatsbyImage)`
      height: 426px;
      width: 426px * ${image.aspectRatio};
    `,
    SkewContainer = styled.div`
      grid-area: 1 / 3 / span 1 / span 1;
      z-index: 2;
      -ms-transform: skew(-22.5deg, 0deg);
      -webkit-transform: skew(-22.5deg, 0deg);
      transform: skew(-22.5deg, 0deg);
    `;

  return (
    <>
      <CourseTypeGrid
        css={
          isEvenIndex
            ? css`
                grid-template-columns: 5% 30% 24% 36% 5%;

                @media only screen and (max-width: ${theme.breakpoints
                    .helper1100}) {
                  grid-template-columns: 3% 31.5% 25.5% 36% 3%;
                }
              `
            : css`
                grid-template-columns: 5% 36% 30% 24% 5%;

                @media only screen and (max-width: ${theme.breakpoints
                    .helper1100}) {
                  grid-template-columns: 3% 36% 31.5% 25.5% 3%;
                }
              `
        }
      >
        <TitleDescriptionLinkContainer
          className="light-bg"
          css={
            isEvenIndex
              ? css`
                  grid-area: 1 / 2 / span 1 / span 1;
                  padding-left: 50px;
                `
              : css`
                  grid-area: 1 / 3 / span 1 / span 1;
                  z-index: 3;
                  padding-right: 50px;
                `
          }
        >
          <CourseTypeHeading>{heading}</CourseTypeHeading>
          <CourseTypeDescription>{description}</CourseTypeDescription>
          <Link4Arrow to={`${link.url}/`}>{link.linkText}</Link4Arrow>
        </TitleDescriptionLinkContainer>
        <BenefitsListContainer
          className="light-bg"
          css={
            isEvenIndex
              ? css`
                  grid-area: 1 / 3 / span 1 / span 1;
                  z-index: 3;
                  padding-left: 50px;
                `
              : css`
                  grid-area: 1 / 4 / span 1 / span 1;
                  padding-right: 50px;
                `
          }
        >
          <ListItemContainer>
            <BulletIconContainer>
              <BulletIcon height="13px" width="13px" />
            </BulletIconContainer>
            <ListItemText >{benefit1}</ListItemText>
          </ListItemContainer>
          <ListItemContainer>
            <BulletIconContainer>
              <BulletIcon height="13px" width="13px" />
            </BulletIconContainer>
            <ListItemText >{benefit2}</ListItemText>
          </ListItemContainer>
          <ListItemContainer>
            <BulletIconContainer>
              <BulletIcon height="13px" width="13px" />
            </BulletIconContainer>
            <ListItemText >{benefit3}</ListItemText>
          </ListItemContainer>
        </BenefitsListContainer>
        <ImgContainer
          className="light-bg"
          css={
            isEvenIndex
              ? css`
                  grid-area: 1 / 4 / span 1 / span 1;
                `
              : css`
                  grid-area: 1 / 2 / span 1 / span 1;
                `
          }
        >
          <StyledImg
            // TODO This particular image is a placeholder image for now.
            // Some additional work may be needed once the final images are ready. -CMS 1/10/20
            image={getImage(image?.gatsbyImageData)}
            alt={image.description}
            // Some of the images will have a graytone while some will be full color.
            className={imageTint && "secondary-bg"}
          />
        </ImgContainer>
        <SkewContainer
          className="light-bg"
          css={
            isEvenIndex
              ? css`
                  transform-origin: bottom left;
                `
              : css`
                  transform-origin: top right;
                `
          }
        />
      </CourseTypeGrid>
      <MobileCourseType
        index={0}
        margin={mobileMargin}
        heading={heading}
        description={description}
        link={link}
        benefit1={benefit1}
        benefit2={benefit2}
        benefit3={benefit3}
        image={image}
        imageTint={true}
      />
    </>
  );
};

CourseTypeBenefits.propTypes = {
  index: PropTypes.number,
  margin: PropTypes.string,
  mobileMargin: PropTypes.string,
  heading: PropTypes.string,
  description: PropTypes.string,
  link: PropTypes.shape({
    url: PropTypes.string,
    linkText: PropTypes.string,
  }),
  benefits1: PropTypes.string,
  benefits2: PropTypes.string,
  benefits3: PropTypes.string,
  image: PropTypes.shape({
    fluid: PropTypes.shape({
      srcSet: PropTypes.string,
      aspectRatio: PropTypes.number,
    }),
    title: PropTypes.string,
    description: PropTypes.string,
  }),
  imageTint: PropTypes.bool,
};

export const query = graphql`
  fragment CourseTypeBenefitsCard on ContentfulBenefitsCard {
    heading
    description
    link {
      url
      linkText
    }
    benefit1
    description1
    benefit2
    description2
    benefit3
    description3
    image {
      gatsbyImageData
    }
  }
`;

export default CourseTypeBenefits;
